import React from 'react';

interface SkeletonProps {
  className?: string;
  width?: string | number;
  height?: string | number;
  aspectRatio?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  width = 'w-full',
  height = 'h-4',
  aspectRatio,
}) => {
  const style: React.CSSProperties = {};

  // Handle numeric dimensions
  if (typeof width === 'number') {
    style.width = `${width}px`;
    width = '';
  }
  if (typeof height === 'number') {
    style.height = `${height}px`;
    height = '';
  }
  if (aspectRatio) {
    style.aspectRatio = aspectRatio;
  }

  return (
    <div
      className={`${width} ${height} bg-gray-200 dark:bg-gray-700 rounded animate-pulse ${className}`}
      style={style}
    />
  );
};

export const QuickActionsSkeleton: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6">
      <Skeleton width="w-32" height="h-6" className="mb-6" />
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4"
          >
            <div className="flex items-start gap-3">
              <Skeleton width="w-10" height="h-10" className="rounded-lg" />
              <div className="flex-1">
                <Skeleton width="w-24" height="h-5" className="mb-2" />
                <Skeleton width="w-full" height="h-4" />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const RepositoriesSkeleton: React.FC = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700">
      <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
        <Skeleton width="w-32" height="h-6" />
        <Skeleton width="w-24" height="h-8" className="rounded" />
      </div>
      <div className="p-6 space-y-4">
        {[1, 2, 3].map((i) => (
          <div
            key={i}
            className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 p-6"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-3">
                  <Skeleton width="w-40" height="h-5" />
                  <Skeleton width="w-16" height="h-5" className="rounded-full" />
                </div>
                <div className="flex items-center gap-4">
                  <Skeleton width="w-20" height="h-3" />
                  <Skeleton width="w-16" height="h-3" />
                  <Skeleton width="w-24" height="h-3" />
                </div>
              </div>
              <Skeleton width="w-6" height="h-6" className="rounded ml-4" />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export const TaskSuggestionsSkeleton: React.FC = () => {
  const taskWidths = ['w-48', 'w-56', 'w-40', 'w-64', 'w-52'];

  return (
    <div className="space-y-4">
      {/* Summary bar */}
      <div className="flex items-center gap-3">
        <Skeleton width="w-44" height="h-4" />
        <Skeleton width="w-20" height="h-5" className="rounded-full" />
        <Skeleton width="w-14" height="h-5" className="rounded-full" />
      </div>

      {/* Table */}
      <div className="bg-white dark:bg-gray-800 rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="flex items-center border-b border-gray-200 dark:border-gray-700">
          <div className="w-10 px-4 py-2.5 shrink-0">
            <Skeleton width="w-3" height="h-3" />
          </div>
          <div className="flex-1 px-4 py-2.5">
            <Skeleton width="w-8" height="h-3" />
          </div>
          <div className="w-28 px-4 py-2.5 shrink-0">
            <Skeleton width="w-14" height="h-3" />
          </div>
          <div className="w-28 px-4 py-2.5 shrink-0 flex justify-end">
            <Skeleton width="w-12" height="h-3" />
          </div>
        </div>

        {/* Rows */}
        {taskWidths.map((tw, i) => (
          <div
            key={i}
            className="flex items-center border-b border-gray-100 dark:border-gray-700/50 last:border-b-0"
          >
            <div className="w-10 px-4 py-3 shrink-0">
              <Skeleton width="w-3" height="h-3" />
            </div>
            <div className="flex-1 px-4 py-3 flex items-center gap-2">
              <Skeleton width="w-4" height="h-4" className="shrink-0" />
              <Skeleton width={tw} height="h-4" />
            </div>
            <div className="w-28 px-4 py-3 shrink-0">
              <Skeleton width="w-14" height="h-5" className="rounded-full" />
            </div>
            <div className="w-28 px-4 py-3 shrink-0 flex justify-end">
              <Skeleton width="w-14" height="h-7" className="rounded-lg" />
            </div>
          </div>
        ))}

        {/* Footer */}
        <div className="px-4 py-3 border-t border-gray-100 dark:border-gray-700/50">
          <Skeleton width="w-40" height="h-3" />
        </div>
      </div>
    </div>
  );
};

export const DashboardSkeleton: React.FC = () => {
  return (
    <div className="min-h-screen">
      <div className="pt-24 px-4 pb-12">
        <div className="max-w-7xl mx-auto">
          {/* Header Skeleton */}
          <div className="mb-8 flex items-start justify-between">
            <div>
              <Skeleton width="w-64" height="h-8" className="mb-2" />
              <Skeleton width="w-96" height="h-6" />
            </div>
            <Skeleton width="w-10" height="h-10" className="rounded-lg" />
          </div>

          {/* Content Skeleton */}
          <div className="space-y-8">
            <QuickActionsSkeleton />
            <RepositoriesSkeleton />
          </div>
        </div>
      </div>
    </div>
  );
};
